import {
  ACTIVE_HURRICANES_GLOBAL_LABEL,
  CURRENT_AIR_QUALITY_LABEL,
  CURRENT_WILDFIRE_INCIDENTS_LABEL,
  DEFAULT_CUSTOM_SOURCE,
  isSensorAlphaSourcePreset,
  isTropicalSourcePreset,
  sanitizeProximityKm,
  type CreateOrgEventAutomationRulePayload,
  type OrgEventAorAssignmentStrategy,
  type OrgEventAutomationCondition,
  type OrgEventAutomationLogicBlock,
  type OrgEventAutomationRule,
  type OrgEventAutomationSource,
  type OrgEventChecklistItemDef,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  buildNotifyConfigPayload,
  DEFAULT_UPDATE_MIN_INTERVAL_MINUTES,
  notifyFieldsFromRule,
} from './automationNotifyForm';
import {
  categoryFieldsFromSaved,
  duplicateWindowHoursFromSaved,
  resolvedCategory,
} from './eventCategory';
import {
  DEFAULT_EVENT_NAME_TEMPLATE,
  toDisplayTemplate,
  toStoredTemplate,
} from './eventNameTemplatePreview';
import {
  feedPresetFromSource,
  createDefaultCondition,
  normalizeAutomationRuleLogic,
  sourceForPreset,
  type AutomationRuleLogicSlice,
  type FeedPreset,
} from './automationRuleLogic';
import { FEED_MIN_CHECK_FREQUENCY, isFeedUri } from './externalFeeds';

export type SourcePreset = FeedPreset;

export type RuleFormValues = {
  name: string;
  enabled: boolean;
  logic: 'all' | 'any';
  source: OrgEventAutomationSource;
  conditions: OrgEventAutomationCondition[];
  logicBlocks: OrgEventAutomationLogicBlock[];
  severity: string;
  eventType: string;
  customCategory: string;
  preventDuplicateEvents: boolean;
  duplicateWindowHours: number;
  notifyOnDataChange: boolean;
  aorStrategy: OrgEventAorAssignmentStrategy;
  aorIds: string[];
  eventName: string;
  thresholdDescription: string;
  /** Check Frequency cron; always one of CHECK_FREQUENCY_PRESETS. */
  repeatPattern: string;
  notifyOnCreate: boolean;
  notificationTypes: string[];
  updateNotificationTypes: string[] | null;
  updateMinIntervalMinutes: number;
  userIds: string[];
  teamIds: string[];
  roleScopeKeys: string[];
  priority: string;
  confirmReceipt: boolean;
  checklistOnCreate: boolean;
  checklistItems: OrgEventChecklistItemDef[];
};

export type SelectOption = { value: string; label: string };

export const FEED_OPTIONS: SelectOption[] = [
  {
    value: 'wildfire_incidents',
    label: CURRENT_WILDFIRE_INCIDENTS_LABEL,
  },
  { value: 'tropical', label: ACTIVE_HURRICANES_GLOBAL_LABEL },
  { value: 'air_quality', label: CURRENT_AIR_QUALITY_LABEL },
];

export type CriterionKind =
  | 'attribute'
  | 'resource_overlap'
  | 'resource_near'
  | 'resource_particular'
  | 'aor_overlap'
  | 'aor_near';

export const CRITERION_OPTIONS: SelectOption[] = [
  { value: 'attribute', label: 'Attribute' },
  { value: 'resource_overlap', label: 'Resources overlap' },
  { value: 'resource_near', label: 'Resources near' },
  { value: 'resource_particular', label: 'Particular Resource' },
  { value: 'aor_overlap', label: 'AOR overlap' },
  { value: 'aor_near', label: 'AOR near' },
];

/** Attribute is omitted unless Attribute Automation Rule Conditions is on. */
export function visibleCriterionOptions(
  showAttribute: boolean
): SelectOption[] {
  if (showAttribute) return CRITERION_OPTIONS;
  return CRITERION_OPTIONS.filter((option) => option.value !== 'attribute');
}

/**
 * AirNow is a numeric threshold feed, so Attribute stays available on it when
 * the attribute flag is off. Every other feed still follows the flag.
 */
export function attributeOfferedForFeed(
  attributeFlagEnabled: boolean,
  preset: SourcePreset
): boolean {
  return attributeFlagEnabled || preset === 'air_quality';
}

export const CRITERION_HELP: {
  kind: CriterionKind;
  label: string;
  help: string;
}[] = [
  {
    kind: 'attribute',
    label: 'Attribute',
    help: 'Match when a field on the data feed meets a threshold, for example storm type at or above Tropical Storm.',
  },
  {
    kind: 'resource_overlap',
    label: 'Resources overlap',
    help: 'Match when at least N of your mapped resources touch the feature (point or polygon).',
  },
  {
    kind: 'resource_near',
    label: 'Resources near',
    help: 'Match when at least N of your mapped resources sit within the distance you set.',
  },
  {
    kind: 'resource_particular',
    label: 'Particular Resource',
    help: 'Match when the selected asset’s location is the point the distance is measured from.',
  },
  {
    kind: 'aor_overlap',
    label: 'AOR overlap',
    help: 'Match when selected Areas of Responsibility touch the feature, even if no individual resource is inside.',
  },
  {
    kind: 'aor_near',
    label: 'AOR near',
    help: 'Match when selected Areas of Responsibility sit within the distance you set.',
  },
];

/** Default km when the user switches a condition to Resources near or AOR near. */
export const DEFAULT_WITHIN_DISTANCE_KM = 10;

export function criterionTypeFromCondition(
  condition: OrgEventAutomationCondition
): CriterionKind {
  if (condition.kind === 'attribute') return 'attribute';
  if (
    condition.kind === 'resource_intersect' &&
    condition.resourceSetId != null
  ) {
    return 'resource_particular';
  }
  const near = sanitizeProximityKm(condition.proximityKm) > 0;
  if (condition.kind === 'aor_intersect') {
    return near ? 'aor_near' : 'aor_overlap';
  }
  return near ? 'resource_near' : 'resource_overlap';
}

function isNearType(kind: CriterionKind): boolean {
  return (
    kind === 'resource_near' ||
    kind === 'resource_particular' ||
    kind === 'aor_near'
  );
}

function isAorType(kind: CriterionKind): boolean {
  return kind === 'aor_overlap' || kind === 'aor_near';
}

function proximityForType(kind: CriterionKind, existingKm?: number): number {
  if (!isNearType(kind)) return 0;
  const current = sanitizeProximityKm(existingKm);
  return current > 0 ? current : DEFAULT_WITHIN_DISTANCE_KM;
}

export const JOIN_OPTIONS: SelectOption[] = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

export const MODE_OPTIONS: SelectOption[] = [
  { value: 'compound', label: 'Combine with previous' },
  { value: 'independent', label: 'New block' },
];

/**
 * Uses only the tokens the description field can tag. Mirrors the firing
 * default in coms `evaluateOrgEventAutomationRule.ts`.
 */
export { DEFAULT_EVENT_NAME_TEMPLATE } from './eventNameTemplatePreview';

export const DEFAULT_THRESHOLD_DESCRIPTION =
  '{resourceCount} resources impacted: {impactedResources}';

/**
 * How often the scheduler re-reads a rule's data source. Matches the
 * `org_event_automation_rules.repeat_pattern` column default.
 */
export const DEFAULT_AUTOMATION_REPEAT_PATTERN = '*/10 * * * *';

export const CHECK_FREQUENCY_PRESETS: SelectOption[] = [
  { value: '* * * * *', label: 'Every 1 minute' },
  { value: '*/5 * * * *', label: 'Every 5 minutes' },
  { value: DEFAULT_AUTOMATION_REPEAT_PATTERN, label: 'Every 10 minutes' },
  { value: '*/30 * * * *', label: 'Every 30 minutes' },
  { value: '0 * * * *', label: 'Every 1 hour' },
  { value: '0 */12 * * *', label: 'Every 12 hours' },
  { value: '0 0 * * *', label: 'Every 24 hours' },
];

export function normalizeCheckFrequency(pattern?: string | null): string {
  const trimmed = pattern?.trim();
  if (
    trimmed &&
    CHECK_FREQUENCY_PRESETS.some((preset) => preset.value === trimmed)
  ) {
    return trimmed;
  }
  return DEFAULT_AUTOMATION_REPEAT_PATTERN;
}

/** True when any condition (or the rule source) reads a non-ArcGIS feed. */
export function conditionsReadFeed(
  conditions: OrgEventAutomationCondition[],
  source?: OrgEventAutomationSource
): boolean {
  return (
    isFeedUri(source?.serviceUrl) ||
    conditions.some((condition) => isFeedUri(condition.source?.serviceUrl))
  );
}

/** CHECK_FREQUENCY_PRESETS is ordered fastest first. */
function presetRank(pattern: string): number {
  return CHECK_FREQUENCY_PRESETS.findIndex(
    (preset) => preset.value === pattern
  );
}

/** Feed rules may not check faster than hourly. */
export function checkFrequencyOptionsFor(
  conditions: OrgEventAutomationCondition[],
  source?: OrgEventAutomationSource
): SelectOption[] {
  if (!conditionsReadFeed(conditions, source)) return CHECK_FREQUENCY_PRESETS;
  const min = presetRank(FEED_MIN_CHECK_FREQUENCY);
  return CHECK_FREQUENCY_PRESETS.filter((_, index) => index >= min);
}

/** Raises a too-fast frequency to hourly when the rule reads a feed. */
export function clampCheckFrequencyFor(
  pattern: string | null | undefined,
  conditions: OrgEventAutomationCondition[],
  source?: OrgEventAutomationSource
): string {
  const normalized = normalizeCheckFrequency(pattern);
  if (!conditionsReadFeed(conditions, source)) return normalized;
  return presetRank(normalized) < presetRank(FEED_MIN_CHECK_FREQUENCY)
    ? FEED_MIN_CHECK_FREQUENCY
    : normalized;
}

export function checkFrequencyLabel(pattern?: string | null): string {
  const normalized = normalizeCheckFrequency(pattern);
  return (
    CHECK_FREQUENCY_PRESETS.find((preset) => preset.value === normalized)
      ?.label ?? 'Every 10 minutes'
  );
}

export const TROPICAL_OCCURRENCE_TEMPLATE = 'tropical:{stormName}';
export const LAYER_OCCURRENCE_TEMPLATE =
  'layer:{layerHash}:feature:{featureId}';
export const SENSOR_OCCURRENCE_TEMPLATE = 'sensor_alpha:{featureId}';

export function logicSliceFromForm(
  values: RuleFormValues
): AutomationRuleLogicSlice {
  return {
    logic: values.logic,
    source: values.source,
    conditions: values.conditions,
    logicBlocks: values.logicBlocks,
  };
}

export function primarySourceFromConditions(
  conditions: OrgEventAutomationCondition[]
): OrgEventAutomationSource {
  return conditions[0]?.source ?? { ...DEFAULT_CUSTOM_SOURCE };
}

export function ruleToForm(rule: OrgEventAutomationRule): RuleFormValues {
  const normalized = normalizeAutomationRuleLogic({
    logic: rule.logic === 'any' ? 'any' : 'all',
    source: rule.source ?? { ...DEFAULT_CUSTOM_SOURCE },
    conditions: Array.isArray(rule.conditions) ? rule.conditions : [],
    logicBlocks: Array.isArray(rule.logic_blocks) ? rule.logic_blocks : [],
  });
  const aorAssignment = rule.event_template?.aorAssignment;
  const primary = primarySourceFromConditions(normalized.conditions);
  const tropical = isTropicalSourcePreset(primary);
  const category = categoryFieldsFromSaved(
    rule.event_template?.type,
    tropical ? 'Tropical Storm' : 'Threshold'
  );

  return {
    name: rule.name,
    enabled: rule.enabled,
    logic: normalized.logic === 'any' ? 'any' : 'all',
    source: normalized.source ?? primary,
    conditions: normalized.conditions,
    logicBlocks: normalized.logicBlocks,
    severity: rule.event_template?.severity || 'High',
    eventType: category.eventType,
    customCategory: category.customCategory,
    preventDuplicateEvents:
      rule.event_template?.preventDuplicateEvents !== false,
    duplicateWindowHours: duplicateWindowHoursFromSaved(
      rule.event_template?.duplicateWindowHours
    ),
    notifyOnDataChange: rule.event_template?.notifyOnDataChange !== false,
    aorStrategy: aorAssignment?.strategy || 'none',
    aorIds: aorAssignment?.aorIds ?? [],
    eventName: toDisplayTemplate(
      rule.event_template?.eventName || DEFAULT_EVENT_NAME_TEMPLATE
    ),
    thresholdDescription: toDisplayTemplate(
      rule.event_template?.thresholdDescription || DEFAULT_THRESHOLD_DESCRIPTION
    ),
    repeatPattern: normalizeCheckFrequency(rule.repeat_pattern),
    ...notifyFieldsFromRule(rule),
    checklistOnCreate: Boolean(rule.checklist_on_create),
    checklistItems: Array.isArray(rule.checklist_config?.items)
      ? rule.checklist_config.items
      : [],
  };
}

/** Editable rule fields: what `formToPayload` produces and the editor saves. */
export type RuleDraftPayload = Omit<
  CreateOrgEventAutomationRulePayload,
  'group_guid' | 'created_by_user_guid'
>;

/** Form values for an unsaved rule payload (e.g. a PRATUS AI draft). */
export function payloadToForm(payload: RuleDraftPayload): RuleFormValues {
  return ruleToForm({
    id: '',
    group_guid: '',
    enabled: false,
    logic: 'all',
    source: { ...DEFAULT_CUSTOM_SOURCE },
    conditions: [],
    event_template: {},
    notify_on_create: false,
    notify_config: {},
    dedupe_key_template: '',
    last_fired_dedupe_key: null,
    scheduler_name: null,
    repeat_pattern: DEFAULT_AUTOMATION_REPEAT_PATTERN,
    created_by_user_guid: null,
    archived: false,
    created_at: '',
    updated_at: '',
    ...payload,
  } as OrgEventAutomationRule);
}

/**
 * Unsaved rule opened from Add Rule. The name stays empty until the user types
 * one.
 */
export function blankRuleForm(userGuid?: string | null): RuleFormValues {
  const condition = createDefaultCondition('tropical');
  return {
    name: '',
    enabled: false,
    logic: 'all',
    source: condition.source ?? { ...DEFAULT_CUSTOM_SOURCE },
    conditions: [condition],
    logicBlocks: [{ type: 'condition', conditionId: condition.id ?? '' }],
    severity: 'High',
    eventType: 'Threshold',
    customCategory: '',
    preventDuplicateEvents: true,
    duplicateWindowHours: 24,
    notifyOnDataChange: true,
    aorStrategy: 'none',
    aorIds: [],
    eventName: toDisplayTemplate(DEFAULT_EVENT_NAME_TEMPLATE),
    thresholdDescription: toDisplayTemplate(DEFAULT_THRESHOLD_DESCRIPTION),
    repeatPattern: DEFAULT_AUTOMATION_REPEAT_PATTERN,
    notifyOnCreate: false,
    notificationTypes: [NotificationType.IN_APP],
    updateNotificationTypes: null,
    updateMinIntervalMinutes: DEFAULT_UPDATE_MIN_INTERVAL_MINUTES,
    userIds: userGuid ? [userGuid] : [],
    teamIds: [],
    roleScopeKeys: [],
    priority: 'watch',
    confirmReceipt: false,
    checklistOnCreate: false,
    checklistItems: [],
  };
}

export function applyPresetToCondition(
  condition: OrgEventAutomationCondition,
  preset: SourcePreset
): OrgEventAutomationCondition {
  const source = sourceForPreset(preset, condition.source);
  if (preset === 'sensor_alpha') {
    return {
      id: condition.id,
      kind: 'attribute',
      field: condition.kind === 'attribute' ? condition.field : 'sensor_value',
      operator: condition.kind === 'attribute' ? condition.operator : '>=',
      value: condition.kind === 'attribute' ? condition.value : '5',
      source,
    };
  }
  if (preset === 'air_quality') {
    return {
      id: condition.id,
      kind: 'attribute',
      field: 'PM25_AQI',
      operator: '>=',
      value: '150',
      source,
    };
  }
  if (preset === 'arcgis_layer') {
    return {
      id: condition.id,
      kind: 'attribute',
      field: condition.kind === 'attribute' ? condition.field : '',
      operator: condition.kind === 'attribute' ? condition.operator : '==',
      value: condition.kind === 'attribute' ? condition.value : '',
      source,
    };
  }
  if (
    condition.kind === 'resource_intersect' ||
    condition.kind === 'aor_intersect'
  ) {
    return {
      ...condition,
      source,
      geometryFrom: preset === 'tropical' ? 'cone' : 'feature',
    };
  }
  if (
    condition.kind === 'attribute' &&
    (preset === 'tropical' || preset === 'wildfire_incidents') &&
    condition.source?.serviceUrl !== source.serviceUrl
  ) {
    return {
      ...condition,
      source,
      field: '',
      operator: '==',
      value: '',
    };
  }
  return { ...condition, source };
}

export function applyCriterionKind(
  condition: OrgEventAutomationCondition,
  kind: CriterionKind
): OrgEventAutomationCondition {
  if (kind === criterionTypeFromCondition(condition)) return condition;
  const preset = feedPresetFromSource(condition.source);
  if (kind === 'attribute') {
    if (preset === 'sensor_alpha') {
      return {
        id: condition.id,
        kind: 'attribute',
        field: 'sensor_value',
        operator: '>=',
        value: '5',
        source: condition.source,
      };
    }
    if (preset === 'air_quality') {
      return {
        id: condition.id,
        kind: 'attribute',
        field: 'PM25_AQI',
        operator: '>=',
        value: '150',
        source: condition.source,
      };
    }
    if (preset === 'arcgis_layer' || preset === 'custom') {
      return {
        id: condition.id,
        kind: 'attribute',
        field: '',
        operator: '==',
        value: '',
        source: condition.source,
      };
    }
    return {
      id: condition.id,
      kind: 'attribute',
      field: 'STORMTYPE',
      operator: '>=',
      value: 'TS',
      source: condition.source,
    };
  }

  const geometryFrom = preset === 'tropical' ? 'cone' : 'feature';
  const existingKm =
    condition.kind === 'resource_intersect' ||
    condition.kind === 'aor_intersect'
      ? condition.proximityKm
      : undefined;
  const proximityKm = proximityForType(kind, existingKm);
  const minCount =
    condition.kind === 'resource_intersect' ||
    condition.kind === 'aor_intersect'
      ? condition.minCount
      : 1;

  if (isAorType(kind)) {
    return {
      id: condition.id,
      kind: 'aor_intersect',
      aorIds: condition.kind === 'aor_intersect' ? condition.aorIds : [],
      minCount,
      geometryFrom,
      proximityKm,
      source: condition.source,
    };
  }

  if (kind === 'resource_particular') {
    return {
      id: condition.id,
      kind: 'resource_intersect',
      minCount: 1,
      geometryFrom,
      proximityKm,
      resourceSetId:
        condition.kind === 'resource_intersect'
          ? (condition.resourceSetId ?? '')
          : '',
      resourceName:
        condition.kind === 'resource_intersect'
          ? condition.resourceName
          : undefined,
      source: condition.source,
    };
  }

  return {
    id: condition.id,
    kind: 'resource_intersect',
    minCount,
    geometryFrom,
    proximityKm,
    source: condition.source,
  };
}

export function formToPayload(
  values: RuleFormValues,
  aorNameById: Map<string, string>
): Omit<
  CreateOrgEventAutomationRulePayload,
  'group_guid' | 'created_by_user_guid'
> {
  const normalized = normalizeAutomationRuleLogic(logicSliceFromForm(values));
  const conditions = normalized.conditions.map((condition) => {
    if (
      condition.kind !== 'resource_intersect' &&
      condition.kind !== 'aor_intersect'
    ) {
      return condition;
    }
    return {
      ...condition,
      proximityKm: sanitizeProximityKm(condition.proximityKm),
    };
  });
  const source = primarySourceFromConditions(conditions);
  const tropical = isTropicalSourcePreset(source);
  const sensor = isSensorAlphaSourcePreset(source);
  const logic: 'all' | 'any' = normalized.logic === 'any' ? 'any' : 'all';

  let region: string | null = null;
  if (values.aorStrategy === 'fixed') {
    const names = values.aorIds
      .map((id) => aorNameById.get(id))
      .filter((n): n is string => Boolean(n));
    region = names.length > 0 ? names.join(', ') : null;
  }

  return {
    name: values.name.trim(),
    enabled: values.enabled,
    logic,
    source,
    conditions,
    logic_blocks: normalized.logicBlocks,
    event_template: {
      severity: values.severity,
      type:
        resolvedCategory(values.eventType, values.customCategory) ||
        (tropical ? 'Tropical Storm' : 'Threshold'),
      preventDuplicateEvents: values.preventDuplicateEvents !== false,
      duplicateWindowHours: duplicateWindowHoursFromSaved(
        values.duplicateWindowHours
      ),
      notifyOnDataChange: values.notifyOnDataChange !== false,
      region,
      status: 'Active',
      eventName:
        toStoredTemplate(values.eventName) || DEFAULT_EVENT_NAME_TEMPLATE,
      thresholdDescription: toStoredTemplate(values.thresholdDescription),
      aiSummary: false,
      aorAssignment: {
        strategy: values.aorStrategy,
        aorIds: values.aorStrategy === 'fixed' ? values.aorIds : [],
      },
    },
    notify_on_create:
      (values.notificationTypes?.length ?? 0) > 0 &&
      (values.userIds?.length ?? 0) +
        (values.teamIds?.length ?? 0) +
        (values.roleScopeKeys?.length ?? 0) >
        0,
    notify_config: buildNotifyConfigPayload(values),
    checklist_on_create: (values.checklistItems?.length ?? 0) > 0,
    checklist_config: {
      items: values.checklistItems ?? [],
    },
    launch_scenario_on_create: false,
    scenario_id: null,
    dedupe_key_template: tropical
      ? TROPICAL_OCCURRENCE_TEMPLATE
      : sensor
        ? SENSOR_OCCURRENCE_TEMPLATE
        : LAYER_OCCURRENCE_TEMPLATE,
    repeat_pattern: clampCheckFrequencyFor(
      values.repeatPattern,
      conditions,
      source
    ),
  };
}
